import { Form, Formik } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';
import ModalComponent from '../../../../components/modal/modal';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../../components/modal/modalFooterComponent';
import CustomDatePicker from '../../../../components/date-picker/customDatePicker';
import FormSelect from '../../../../components/dropdown/formSelect';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';
import { type Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto } from '@wealthlane/api-services/wealthlane-identity-service';
import { type AdvisorApiKeyFormValues } from './CreateAdvisorApiKeyModal';
import {
  calculateEffectiveTo,
  EXPIRATION_PRESET_OPTIONS,
  inferExpirationPreset,
} from '../expirationUtils';

interface EditAdvisorApiKeyModalProps {
  isOpen: boolean;
  isLoading: boolean;
  credential?: Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto;
  onClose: () => void;
  onSubmit: (values: AdvisorApiKeyFormValues) => void;
}

const validationSchema = Yup.object().shape({
  effectiveFrom: Yup.string().required('Effective from is required'),
  expirationPreset: Yup.string().required('Expiration is required'),
  effectiveTo: Yup.string()
    .nullable()
    .when('expirationPreset', {
      is: 'custom',
      then: (schema) =>
        schema
          .required('End date is required for a custom expiration')
          .test('after-effective-from', 'End date must be on or after effective from', function (value) {
            const { effectiveFrom } = this.parent as AdvisorApiKeyFormValues;
            if (!value || !effectiveFrom) return true;
            return moment(value).isSameOrAfter(moment(effectiveFrom), 'day');
          }),
      otherwise: (schema) => schema.nullable(),
    }),
});

const EditAdvisorApiKeyModal = ({
  isOpen,
  isLoading,
  credential,
  onClose,
  onSubmit,
}: EditAdvisorApiKeyModalProps): JSX.Element => {
  if (!isOpen) return <></>;

  const effectiveFrom = credential?.effectiveFrom
    ? moment(credential.effectiveFrom).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');
  const effectiveTo = credential?.effectiveTo
    ? moment(credential.effectiveTo).format('YYYY-MM-DD')
    : '';
  const { preset, customEffectiveTo } = inferExpirationPreset(effectiveFrom, effectiveTo);

  const initialValues: AdvisorApiKeyFormValues = {
    effectiveFrom,
    expirationPreset: preset,
    effectiveTo: customEffectiveTo,
  };

  return (
    <ModalComponent
      id='editAdvisorApiKeyModal'
      show={isOpen}
      closeModal={onClose}
      title={`Edit API Key Dates — ${credential?.apiKeyPrefix ?? '-'}`}
      size='md'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmit({
            ...values,
            effectiveTo: calculateEffectiveTo(
              values.effectiveFrom,
              values.expirationPreset,
              values.effectiveTo,
            ),
          });
        }}>
        {({ touched, handleChange, errors, values, setFieldValue }) => {
          const computedEndDate =
            values.expirationPreset !== 'custom' && values.expirationPreset !== 'no_expiration'
              ? calculateEffectiveTo(values.effectiveFrom, values.expirationPreset)
              : null;

          return (
            <Form id='editAdvisorApiKeyForm'>
              <ModalBodyComponent>
                <div className='grid grid-cols-1 gap-4'>
                  <CustomDatePicker
                    label='Effective From'
                    name='effectiveFrom'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    required
                  />
                  <FormSelect
                    label='Expiration'
                    name='expirationPreset'
                    options={[...EXPIRATION_PRESET_OPTIONS]}
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={(field, value) => {
                      void setFieldValue(field, value);
                      if (value !== 'custom') {
                        void setFieldValue('effectiveTo', '');
                      }
                    }}
                    required
                  />
                  {values.expirationPreset === 'custom' && (
                    <CustomDatePicker
                      label='End Date'
                      name='effectiveTo'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setFieldValue={setFieldValue}
                      required
                    />
                  )}
                  {computedEndDate && (
                    <Heading
                      variant='body-xs'
                      color={COLORS.SpanishGray}
                      title={`This key will expire on ${moment(computedEndDate).format('MMM D, YYYY')}.`}
                    />
                  )}
                  {values.expirationPreset === 'no_expiration' && (
                    <Heading
                      variant='body-xs'
                      color={COLORS.SpanishGray}
                      title='This key will not expire automatically. Revoke it manually when it is no longer needed.'
                    />
                  )}
                </div>
              </ModalBodyComponent>
              <ModalFooterComponent
                onCloseModal={onClose}
                isEditShow={false}
                modalStatus='Edit'
                buttonTitle='Save'
                isLoading={isLoading}
              />
            </Form>
          );
        }}
      </Formik>
    </ModalComponent>
  );
};

export default EditAdvisorApiKeyModal;
